import { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Col,
  Form,
  Modal,
  Row,
  Table,
  Input,
  Checkbox,
  notification,
  message,
} from "antd";

import { RecipeCategory } from "../Models/RecipeCategory";

import { recipeCategoryService } from "../Services/RecipeCategory.service";
import { CheckOutlined, DownOutlined, UpOutlined } from "@ant-design/icons";
import { useForm } from "antd/es/form/Form";

export function RecipeCategoriesPage() {
  const [updateFormInstance] = useForm();

  const createForm: any = useRef({});
  const updateForm: any = useRef({});

  const [dataSource, setDataSource] = useState<RecipeCategory[]>([]);

  const [isShowingCreateModal, setIsShowingCreateModal] = useState(false);
  const [isShowingUpdateModal, setIsShowingUpdateModal] = useState<
    number | null
  >(null);
  const [isFetching, setIsFetching] = useState(false);
  const [filterQuery, setFilterQuery] = useState("");

  const filteredDatasource = useMemo(() => {
    return dataSource.filter((recipeCategory) =>
      recipeCategory.name.toLowerCase().includes(filterQuery.toLowerCase())
    );
  }, [dataSource, filterQuery]);

  const activeUpdateModel = useMemo(
    () =>
      dataSource.find(
        (recipeCategory) => recipeCategory.id === isShowingUpdateModal
      ),
    [dataSource, isShowingUpdateModal]
  );

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    setIsFetching(true);

    try {
      const recipeCategories: RecipeCategory[] =
        await recipeCategoryService.getRecipeCategories();
      setDataSource(recipeCategories);
      setIsFetching(false);
    } catch (error) {
      setIsFetching(false);
    }
  }

  const columns = [
    {
      title: "Navn",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Rækkefølge",
      dataIndex: "order",
      key: "order",
      align: "center" as any,
      width: 80,
    },
    {
      title: "Op",
      dataIndex: "",
      key: "moveUp",
      align: "center" as any,
      width: 40,
      render: (_: any, record: any) => (
        <Button
          type="link"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();

            if (record.order === 0) return;
            // Handle move up action
            moveUp(record);
          }}
        >
        <UpOutlined />
        </Button>
      ),
    },
    {
      title: "Ned",
      dataIndex: "",
      key: "moveDown",
      align: "center" as any,
      width: 40,
      render: (_: any, record: any) => (
        <Button
          type="link"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();

            if (record.order + 1 > dataSource.length - 1) return;
            // Handle move down action
            moveDown(record);
          }}
        >
          
          <DownOutlined />
        </Button>
      ),
    },
  ];

  /**
   * Update modal
   */

  function openUpdateModal(id: number) {
    setIsShowingUpdateModal(id);

    const activeIngredient = dataSource.find(
      (ingredient) => ingredient.id === id
    );

    updateFormInstance.setFieldsValue(activeIngredient);
  }

  function closeUpdateModal() {
    setIsShowingUpdateModal(null);
    updateFormInstance.resetFields();
  }

  /**
   * Create modal
   */
  function openCreateModal() {
    setIsShowingCreateModal(true);
  }

  function closeCreateModal() {
    setIsShowingCreateModal(false);
    createForm.current.resetFields();
  }

  function handleCreateAction() {
    const form = createForm.current;
    form.submit();
  }

  async function moveUp(currentItem: RecipeCategory) {
    const mappedData = dataSource.map((recipeCategory) => {
      if (recipeCategory.id === currentItem.id) {
        return {
          ...recipeCategory,
          order: recipeCategory.order - 1,
        };
      }

      if (recipeCategory.order === currentItem.order - 1) {
        return {
          ...recipeCategory,
          order: recipeCategory.order + 1,
        };
      }

      return recipeCategory;
    });

    await recipeCategoryService.updateRecipeCategory(mappedData);
    await fetchData();
  }

  async function moveDown(currentItem: RecipeCategory) {
    const mappedData = dataSource.map((recipeCategory) => {
      if (recipeCategory.id === currentItem.id) {
        return {
          ...recipeCategory,
          order: recipeCategory.order + 1,
        };
      }

      if (recipeCategory.order === currentItem.order + 1) {
        return {
          ...recipeCategory,
          order: recipeCategory.order - 1,
        };
      }

      return recipeCategory;
    });

    await recipeCategoryService.updateRecipeCategory(mappedData);
    await fetchData();
  }

  async function onUpdateSubmit(values: any) {
    try {
      const success = await recipeCategoryService.updateRecipeCategory([
        values,
      ]);

      if (!!success) {
        await fetchData();
        closeUpdateModal();

        notification.open({
          message: "Sådan!",
          description: "Opskriftskategorien er blevet opdateret",
        });
      }
    } catch (error) {
      throw error;
    }
  }

  function onUpdateSubmitFailed(errorInfo: any) {
    console.error("Failed:", errorInfo);
  }

  async function onCreateSubmit(values: any) {
    values.order = dataSource.length;
    const success = await recipeCategoryService.createRecipeCategory(values);

    if (!!success) {
      notification.open({
        message: "Sådan!",
        description: "Opskriftskategorien er blevet oprettet",
      });

      await fetchData();
      closeCreateModal();
    }
  }

  function onCreateSubmitFailed(errorInfo: any) {
    console.error("Failed:", errorInfo);
  }

  async function onDelete() {
    if (!activeUpdateModel?.id) return;

    const success = await recipeCategoryService.deleteRecipeCategory(
      activeUpdateModel,
      dataSource
    );

    if (!!success) {
      closeUpdateModal();
      fetchData();

      message.success(`"${activeUpdateModel?.name}" er blevet slettet`)
    }
  }

  const handleFilterQuerychange = (event: any) => {
    setFilterQuery(event.target.value);
  };

  return (
    <>
      <Row>
        <Col flex={1}>
          <Input
            placeholder="Find opskriftskategori"
            value={filterQuery}
            onChange={handleFilterQuerychange}
          />
        </Col>
        <Col offset={1}>
          <Button type="primary" onClick={openCreateModal}>
            Opret Opskriftskategori
          </Button>
        </Col>
      </Row>

      <Row style={{ marginTop: "16px" }}>
        <Col span={24}>
          <Table
            bordered={true}
            dataSource={filteredDatasource}
            columns={columns}
            loading={isFetching}
            rowKey={(record) => record.id.toString()}
            onRow={(record, rowIndex) => {
              return {
                onClick: (event) => {
                  openUpdateModal(record.id);
                },
              };
            }}
          />
        </Col>
      </Row>

      {/* Update modal */}

      <Modal
        title={`Rediger "${activeUpdateModel?.name}"`}
        open={!!isShowingUpdateModal}
        okText="Opdater"
        cancelText="Annuller"
        onCancel={closeUpdateModal}
        footer={[
          <Row key="recipe-categories-update-footer">
            <Button
              key="delete"
              danger
              onClick={() => {
                Modal.confirm({
                  title: "Er du sikker?",
                  content: `Er du sikker på, at du vil slette "${activeUpdateModel?.name}"?`,
                  okText: "Ja, jeg er sikker",
                  cancelText: "Annuller",
                  onOk: () => {
                    onDelete();
                  },
                });
              }}
            >
              Slet
            </Button>
            <span key="spacer" style={{ flex: 1 }} />
            <Button
              key="cancel"
              onClick={() => {
                closeUpdateModal();
              }}
            >
              Annuller
            </Button>
            <Button
              key="update"
              type="primary"
              onClick={() => {
                onUpdateSubmit(updateFormInstance.getFieldsValue());
              }}
            >
              Opdater
            </Button>
          </Row>,
        ]}
      >
        <Form
          form={updateFormInstance}
          ref={updateForm}
          name="update-data"
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
          style={{ maxWidth: 600 }}
          onFinish={onUpdateSubmit}
          onFinishFailed={onUpdateSubmitFailed}
          layout="vertical"
          autoComplete="off"
          initialValues={activeUpdateModel}
        >
          <Form.Item hidden={true} name="id">
            <Input disabled={true} />
          </Form.Item>
          {/* Name */}
          <Form.Item
            label="Navn"
            name="name"
            rules={[{ required: true, message: "Feltet skal udfyldes" }]}
          >
            <Input />
          </Form.Item>

          {/* Order */}
          <Form.Item hidden={true} name="order">
            <Input disabled={true} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Create modal */}
      <Modal
        title="Opret opskriftskategori"
        open={isShowingCreateModal}
        okText="Opret"
        cancelText="Annuller"
        onOk={handleCreateAction}
        // confirmLoading={confirmLoading}
        onCancel={closeCreateModal}
      >
        <Form
          ref={createForm}
          name="create-data"
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
          style={{ maxWidth: 600 }}
          onFinish={onCreateSubmit}
          onFinishFailed={onCreateSubmitFailed}
          layout="vertical"
          autoComplete="off"
        >
          {/* Name */}
          <Form.Item
            label="Navn"
            name="name"
            rules={[{ required: true, message: "Feltet skal udfyldes" }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
